import React from 'react';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';

const u = (id, w = 1600) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=85`;

const Craftsmanship = () => {
  return (
    <div>
      <PageHeader
        eyebrow="Craftsmanship"
        title="The hands behind the hem."
        intro="At Andobr\u00e9e, a garment is never the work of one person. It is the sum of quiet decisions \u2014 a mill master selecting a yarn, a tailor measuring a shoulder, a finisher pressing a seam for the third time."
      />

      {/* Process steps */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-[1300px] mx-auto space-y-24">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${
                i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''
              }`}
            >
              <Reveal variant="image" duration={1400}>
                <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={140}>
                <p className="font-display text-6xl text-neutral-300 mb-4">0{i + 1}</p>
                <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4">{s.eyebrow}</p>
                <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-6 leading-tight">
                  {s.title}
                </h2>
                <p className="text-[15px] leading-relaxed text-neutral-600 max-w-md">{s.body}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* Partner houses */}
      <section className="py-24 px-6 lg:px-12 bg-[#fafaf8]">
        <div className="max-w-[1400px] mx-auto text-center">
          <Reveal variant="fade">
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4">
              Partner Houses
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mb-14">
              We work with people, not factories.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <div>
                  <p className="font-display text-2xl mb-1">{p.name}</p>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-neutral-500">{p.place}</p>
                  <p className="mt-3 text-sm text-neutral-600">{p.specialty}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const STEPS = [
  {
    eyebrow: 'Step One',
    title: 'The yarn is chosen.',
    body: 'Every season begins at the mill. We visit each partner in person, handle the raw yarn, and reject more than we accept. Only the finest spools leave with us.',
    image: u('photo-1603906650843-b58e94d9df4d'),
  },
  {
    eyebrow: 'Step Two',
    title: 'The pattern is cut.',
    body: 'Patterns are cut by hand in our Florence atelier. A single coat can take three days on the cutting table \u2014 and our cutters have been doing this for decades.',
    image: u('photo-1581504912285-745af0d408f3'),
  },
  {
    eyebrow: 'Step Three',
    title: 'The garment is built.',
    body: 'A jacket passes through up to twenty-two pairs of hands. Collars are pick-stitched by hand. Linings are tacked, not glued. A piece should hold its shape for decades.',
    image: u('photo-1659097617978-1a1aba8ae7ac'),
  },
  {
    eyebrow: 'Step Four',
    title: 'It is pressed, checked, and signed.',
    body: 'Finishers press each seam three times, water and heat. The final inspection is done by one of two people \u2014 and every piece is numbered before it leaves us.',
    image: u('photo-1474432575338-c24a3becef7d'),
  },
];

const PARTNERS = [
  { name: 'Casa Bianchi', place: 'Biella, Italy', specialty: 'Wool & cashmere milling since 1911' },
  { name: 'Atelier Durand', place: 'Paris, France', specialty: 'Silk weaving & evening-wear finishing' },
  { name: 'Tessitura Mari', place: 'Como, Italy', specialty: 'Jacquard silks and satins' },
  { name: 'Pellicceria LXV', place: 'Florence, Italy', specialty: 'Vegetable-tanned calfskin leather' },
];

export default Craftsmanship;
